import {
  READER_ID_PARAM,
  RETURN_TO_PARAM,
} from "@bindery/shared/contracts/session";
import { peersFor, targetOfHost } from "@bindery/topology";
import { READER_SESSION_PATH } from "@/contracts/session";

export const SIGN_IN_PAGE = "/sign-in";
export const BROKER_PARENT_PATH = "/parent";

// Sign-in and management are not links: they go through ./actions, which
// prove the reader came from this app.

function withReturnTo(path: string, returnTo: string): string {
  return `${path}?${new URLSearchParams({ [RETURN_TO_PARAM]: returnTo }).toString()}`;
}

/** "Save progress": the /sign-in page, which reads the reader from the session. */
export function saveProgressHref(returnTo: string): string {
  return withReturnTo(SIGN_IN_PAGE, returnTo);
}

// The same topology row the Worker's auth consumer defaults to; a host the
// topology does not name (localhost) gets the dev peer.
export function brokerOrigin(hostname: string): string {
  return peersFor(targetOfHost(hostname) ?? "dev").authOrigin;
}

/** Needs no sign-in state, so a plain link to the broker's /parent. */
export function parentHref(hostname: string = location.hostname): string {
  return new URL(BROKER_PARENT_PATH, brokerOrigin(hostname)).toString();
}

/**
 * The device's identify-only entry: back to the device's own reader, else
 * none and the Worker mints one. The device's reader rides a same-origin
 * form POST, so its id reaches no URL, history entry or log line; the
 * Worker lands it ungated, unlike a scanned bookmark's GET.
 */
export type ReaderEntry =
  | { method: "get"; href: string }
  | { method: "post"; action: string; fields: Record<string, string> };

export function readerEntry(
  deviceReaderId: string | null,
  returnTo?: string,
): ReaderEntry {
  const params = new URLSearchParams();
  if (returnTo) params.set(RETURN_TO_PARAM, returnTo);
  if (deviceReaderId) {
    return {
      method: "post",
      action: READER_SESSION_PATH,
      fields: {
        [READER_ID_PARAM]: deviceReaderId,
        ...Object.fromEntries(params),
      },
    };
  }
  const query = params.toString();
  return {
    method: "get",
    href:
      query === "" ? READER_SESSION_PATH : `${READER_SESSION_PATH}?${query}`,
  };
}
